// @ts-strict-ignore
import React from 'react';
import { FeatureSection, Nullable, ReleaseStepComponentProps } from '../../typescriptHelpers';
import { antGrey, runwayColors } from '../styles/globalStyles';
import BranchingDiagram from './BranchingDiagram';
import PullRequestComponent from './PullRequestComponent';
import AutomatedByRunwayIcon from '../AutomatedByRunwayIcon';
import Link from 'antd/lib/typography/Link';
import orangeDot from '../../../assets/orange-dot-8.svg';
import Divider from 'antd/lib/divider';
import {
    CodeVersionData,
    KickoffActionStatus,
    PullRequest,
    PullRequestStatus,
    ReleaseType,
    ResolvedFeatureAffiliation,
} from '../../types';
import Tooltip from 'antd/lib/tooltip';
import { iconAndAltForIntegrationType } from '../../helpers/integrationHelpers';
import Text from 'antd/lib/typography/Text';
import Space from 'antd/lib/space';
import Button from 'antd/lib/button';
import {
    displayNameForFeatureAffiliation,
    displayTextForBumpVersionStatus,
    displayTextForCodePromotionStatus,
} from '../../helpers';
import { useSelector } from 'react-redux';
import { RootState } from '../../reducers/reducers';
import { InfoCircleOutlined, PullRequestOutlined, LinkOutlined } from '@ant-design/icons';
import { selectRelease } from '../../selectors/selectors';
import { EmptyStateWrapper } from '../releaseEmptyState/EmptyStateWrapper';
import Skeleton from 'antd/lib/skeleton/Skeleton';

const moduleTitleStyle: React.CSSProperties = {
    fontSize: 13,
};

export interface KickoffInfoProps extends ReleaseStepComponentProps {
    releaseType: ReleaseType;
    releaseBranch: string;
    doesReleaseBranchExist: boolean;
    didRunwayCreateReleaseBranch: boolean;
    isStaticReleaseBranch: boolean;
    resolvedFeatureAffiliations: ResolvedFeatureAffiliation[];
    previousTaggedReleaseTag?: string;
    previousTaggedReleaseTagVersionsBehind: number;
    previousReleaseVersion?: string;
    previousReleaseTag?: string;
    releaseBranchUrl?: string;
    releaseBranchDiffUrl?: string;
    workflowBranchDiffUrl?: string;
    codePromotionStatus?: KickoffActionStatus;
    codePromotionPullRequest?: PullRequest;
    canPerformCodePromotion: boolean;
    hasPerformedAnyCodePromotion: boolean;
    workingBranchCodeVersionData?: CodeVersionData;
    releaseBranchCodeVersionData?: CodeVersionData;
    hotfixBranchingBase?: string; // hotfixes branch of prev release tag (if possible)
    workingBranch: Nullable<string>;
    releasePlanningSummary: Nullable<string>;
    canBeSubmitted: Nullable<boolean>;
}

const KickoffInfo: React.FC<KickoffInfoProps> = (props) => {
    const releaseIsHotfix = props.releaseType === ReleaseType.Hotfix;
    const featureAffiliations = props.resolvedFeatureAffiliations;
    const usesStaticReleaseBranch = props.isStaticReleaseBranch;
    const hasPerformedAnyCodePromotion = props.hasPerformedAnyCodePromotion;
    const codePromotionPullRequest = props.codePromotionPullRequest;
    const codePromotionStatus = props.codePromotionStatus;
    const releaseBranchExists = props.doesReleaseBranchExist;
    const releaseBranchDiffUrl = props.releaseBranchDiffUrl;
    const releaseBranchUrl = props.releaseBranchUrl;
    const hotfixBranchingBase = props.hotfixBranchingBase;
    const didRunwayCreateReleaseBranch = props.didRunwayCreateReleaseBranch;
    const previousTaggedReleaseTag = props.previousTaggedReleaseTag;
    const previousTagVersionsBehind = props.previousTaggedReleaseTagVersionsBehind;
    const releaseBranchCodeData = props.releaseBranchCodeVersionData;
    const workingBranchCodeData = props.workingBranchCodeVersionData;

    const isKickedOff = useSelector((state: RootState) => {
        const release = selectRelease(state, props.releaseId);

        return release.isKickedOff;
    });

    const workingBranch = props.workingBranch;
    const releaseBranch = props.releaseBranch;
    const isWorkingBranchSameAsReleaseBranch = releaseBranch && workingBranch && releaseBranch === workingBranch;

    const isCodePromotionDetected = codePromotionStatus === KickoffActionStatus.Detected;
    const branchingBase = releaseIsHotfix ? hotfixBranchingBase : workingBranch;
    const isPreviousReleaseTagUpToDate = previousTagVersionsBehind === 0;
    const shouldShowVersionInCodeComponentForReleaseBranch = !isWorkingBranchSameAsReleaseBranch && releaseBranchExists;

    const shouldShowBumpStatusComponentForReleaseBranch =
        isKickedOff && shouldShowVersionInCodeComponentForReleaseBranch && !releaseBranchCodeData.errorMessage;

    const shouldShowBumpStatusComponentForWorkingBranch =
        (!shouldShowBumpStatusComponentForReleaseBranch && !workingBranchCodeData.errorMessage) ||
        (workingBranchCodeData?.bumpVersionStatus === KickoffActionStatus.Incomplete &&
            !workingBranchCodeData.errorMessage);

    const displayCodeVersionTooltipCopy: (branch: string) => string = (branch: string) => {
        return `The current version in code on your ${branch} based on the value found in the version file(s) defined for this app. You can view and edit your app's version file(s) in App Settings.`;
    };

    const renderFeatureAffiliations = (featureAffiliations: ResolvedFeatureAffiliation[]) => {
        if (featureAffiliations.length === 0) {
            return <Text style={{ color: runwayColors.statusYellow }}>Not detected</Text>;
        }

        return featureAffiliations.map((fa, index) => {
            const [icon, alt] = iconAndAltForIntegrationType(fa.integrationId);

            return (
                <Space key={index}>
                    <img src={icon} alt={alt} />
                    <Text type="secondary">{displayNameForFeatureAffiliation(fa.affiliationType)}</Text>
                    <Text code>{fa.resolvedString}</Text>
                </Space>
            );
        });
    };

    const renderPullRequest = (pullRequest: PullRequest) => {
        if (!pullRequest) {
            return null;
        }
        const isPullRequestOpen = pullRequest.status === PullRequestStatus.Open;
        const isPullRequestMerged = pullRequest.status === PullRequestStatus.Merged;

        return (
            <Space style={{ marginTop: 8 }} size={8}>
                <Space size={6}>
                    {isPullRequestMerged && <LinkOutlined style={{ color: antGrey.grey7 }} />}
                    {isPullRequestOpen && <PullRequestOutlined style={{ color: antGrey.grey7 }} />}
                    {isPullRequestOpen && <Text style={{ color: antGrey.grey8 }}>PR #{pullRequest.number}</Text>}
                    {isPullRequestMerged && (
                        <Link href={pullRequest.url} target="_blank">
                            PR #{pullRequest.number}
                        </Link>
                    )}
                </Space>
                {isPullRequestOpen && (
                    <Button
                        href={pullRequest.url}
                        target="_blank"
                        icon={<img src={orangeDot} style={{ marginRight: 6 }} />}
                    >
                        Resolve
                    </Button>
                )}
            </Space>
        );
    };

    const featureAffiliationsComponent = (isInEmptyState: boolean) => {
        return (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
                <div style={{ display: 'flex', marginBottom: 16, alignItems: 'center' }}>
                    <Text style={{ ...moduleTitleStyle, marginRight: 8 }} type="secondary">
                        Feature affiliations
                    </Text>
                    <Tooltip title="These are the labels / fix versions associated with ticketed work for this release.">
                        <InfoCircleOutlined style={{ color: runwayColors.info }} />
                    </Tooltip>
                </div>
                {isInEmptyState ?
                    <Skeleton paragraph={false} />
                :   <>
                        <Space direction="vertical">{renderFeatureAffiliations(featureAffiliations)}</Space>
                    </>
                }
            </div>
        );
    };

    const releaseBranchComponent = (isInEmptyState: boolean) => {
        return (
            <div style={{ display: 'flex' }}>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <div style={{ marginBottom: 16 }}>
                        <Text style={{ ...moduleTitleStyle, marginRight: 8 }} type="secondary">
                            Release branch
                        </Text>
                        <Tooltip
                            title={
                                usesStaticReleaseBranch ?
                                    'This is the branch that your release will be built from.'
                                :   'Your release branch is the branch that your release will be built from.'
                            }
                        >
                            <InfoCircleOutlined style={{ color: runwayColors.info }} />
                        </Tooltip>
                    </div>

                    {isInEmptyState ?
                        <Skeleton
                            title={false}
                            paragraph={{
                                rows: 2,
                            }}
                        />
                    :   <>
                            <BranchingDiagram
                                usesStaticReleaseBranch={usesStaticReleaseBranch}
                                hasPerformedAnyCodePromotion={hasPerformedAnyCodePromotion}
                                releaseBranchExists={releaseBranchExists}
                                base={branchingBase}
                                releaseBranch={releaseBranch}
                                releaseBranchUrl={releaseBranchUrl}
                                isHotfix={releaseIsHotfix}
                            />
                        </>
                    }
                </div>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-end',
                        justifyContent: 'flex-start',
                        marginLeft: 8,
                        flexGrow: 1,
                    }}
                >
                    {!usesStaticReleaseBranch && releaseBranchExists && (
                        <Space>
                            {didRunwayCreateReleaseBranch && <AutomatedByRunwayIcon />}
                            <Text
                                style={{
                                    color: runwayColors.statusGreen,
                                    textAlign: 'right',
                                }}
                            >
                                Detected
                            </Text>
                        </Space>
                    )}

                    {!isInEmptyState && (
                        <>
                            {!usesStaticReleaseBranch && !releaseBranchExists && (
                                <Text
                                    style={{
                                        color: runwayColors.statusYellow,
                                        textAlign: 'right',
                                    }}
                                >
                                    Not detected
                                </Text>
                            )}
                        </>
                    )}
                    {usesStaticReleaseBranch &&
                        codePromotionStatus &&
                        workingBranch &&
                        !isWorkingBranchSameAsReleaseBranch && (
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'flex-end',
                                }}
                            >
                                <Space>
                                    {hasPerformedAnyCodePromotion && <AutomatedByRunwayIcon />}
                                    <Text
                                        style={{
                                            textAlign: 'right',
                                            color:
                                                isCodePromotionDetected ?
                                                    runwayColors.statusGreen
                                                :   runwayColors.statusYellow,
                                        }}
                                    >
                                        {displayTextForCodePromotionStatus(codePromotionStatus)}
                                    </Text>
                                </Space>
                                {codePromotionPullRequest && <PullRequestComponent {...codePromotionPullRequest} />}
                            </div>
                        )}
                </div>
            </div>
        );
    };

    const previousTagComponent = (isInEmptyState: boolean) => {
        return (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
                <div style={{ display: 'flex', marginBottom: 16, alignItems: 'center' }}>
                    <Text style={{ ...moduleTitleStyle, marginRight: 8 }} type="secondary">
                        Previous release tag
                    </Text>
                </div>
                <Space direction="vertical">
                    {previousTaggedReleaseTag && (
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                            }}
                        >
                            <Space size={6}>
                                <Text code>{previousTaggedReleaseTag}</Text>
                                {isKickedOff && releaseBranchDiffUrl && (
                                    <Space size={6}>
                                        <LinkOutlined style={{ color: antGrey.grey7 }} />
                                        <Link href={releaseBranchDiffUrl} target="_blank">
                                            Code diff
                                        </Link>
                                    </Space>
                                )}
                            </Space>
                            {!isPreviousReleaseTagUpToDate && (
                                <Text
                                    style={{
                                        color: runwayColors.statusYellow,
                                        maxWidth: '50%',
                                        textAlign: 'right',
                                    }}
                                >
                                    {previousTagVersionsBehind} {previousTagVersionsBehind > 1 ? 'versions' : 'version'}{' '}
                                    behind
                                </Text>
                            )}
                        </div>
                    )}

                    {isInEmptyState ?
                        <Skeleton paragraph={false} />
                    :   <>
                            {!previousTaggedReleaseTag && (
                                <Text style={{ color: runwayColors.statusYellow }}>Not detected</Text>
                            )}
                        </>
                    }
                </Space>
            </div>
        );
    };

    const bumpVersionStatusComponent = (bumpStatus: KickoffActionStatus, bumpPR?: PullRequest, bumpErrMsg?: string) => {
        return (
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-end',
                    justifyContent: 'flex-start',
                    marginLeft: 8,
                    flexGrow: 1,
                }}
            >
                <Space>
                    {bumpStatus !== KickoffActionStatus.NotDetected && bumpPR && <AutomatedByRunwayIcon />}
                    <Text
                        style={{
                            color:
                                bumpStatus === KickoffActionStatus.Detected ?
                                    runwayColors.statusGreen
                                :   runwayColors.statusYellow,
                            textAlign: 'right',
                        }}
                    >
                        {!bumpErrMsg && displayTextForBumpVersionStatus(bumpStatus)}
                    </Text>
                </Space>
                {bumpStatus === KickoffActionStatus.Incomplete && bumpPR && renderPullRequest(bumpPR)}
            </div>
        );
    };

    const versionInCodeComponent = (
        branchNameFallback: string,
        shouldShowBumpStatusComponent: boolean,
        codeVersionData?: CodeVersionData,
        isInEmptyState?: boolean,
    ) => {
        return (
            <div style={{ display: 'flex' }}>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <div style={{ marginBottom: 16 }}>
                        <Text style={{ ...moduleTitleStyle, marginRight: 8 }} type="secondary">
                            Version in code on
                            {` ${
                                codeVersionData?.branchName && codeVersionData?.branchName != '' ?
                                    `'` + codeVersionData?.branchName + `'`
                                :   branchNameFallback
                            }`}
                        </Text>
                        <Tooltip title={displayCodeVersionTooltipCopy(branchNameFallback)}>
                            <InfoCircleOutlined style={{ color: runwayColors.info }} />
                        </Tooltip>
                    </div>
                    {isInEmptyState ?
                        <Skeleton paragraph={false} />
                    :   <>
                            <Text
                                code={!!codeVersionData?.currentCodeVersionExternalString}
                                style={{
                                    color:
                                        codeVersionData?.currentCodeVersionExternalString ? null : (
                                            runwayColors.statusYellow
                                        ),
                                }}
                            >
                                {
                                    // if errorMessage is present, display it, otherwise display the currentCodeVersionExternalString
                                    codeVersionData?.errorMessage ?
                                        codeVersionData?.errorMessage
                                    :   codeVersionData?.currentCodeVersionExternalString
                                }
                            </Text>
                        </>
                    }
                </div>
                {shouldShowBumpStatusComponent &&
                    codeVersionData &&
                    bumpVersionStatusComponent(
                        codeVersionData.bumpVersionStatus,
                        codeVersionData.bumpVersionPullRequest,
                    )}
            </div>
        );
    };

    const cardStyle: React.CSSProperties = {
        backgroundColor: antGrey.grey3,
        borderRadius: 6,
        paddingTop: 16,
        paddingBottom: 16,
        paddingLeft: 24,
        paddingRight: 24,
        flexGrow: 1,
    };

    const cardContainerStyle: React.CSSProperties = {
        flexShrink: 0,
        display: 'flex',
        minWidth: 300,
    };

    return (
        <EmptyStateWrapper
            releaseStepType={props.releaseStepType}
            appId={props.appId}
            featureSections={[
                FeatureSection.KickoffFeatureAffiliation,
                FeatureSection.KickoffReleaseBranch,
                FeatureSection.KickoffVersionInCode,
                FeatureSection.KickoffPreviousReleaseTag,
            ]}
            releaseId={props.releaseId}
        >
            {(isInEmptyState) => (
                <div
                    style={{
                        ...cardContainerStyle,
                        marginRight: 24,
                        marginBottom: 24,
                        width: 'calc(50% - 24px)',
                    }}
                >
                    <div style={{ ...cardStyle }}>
                        {featureAffiliationsComponent(isInEmptyState)}
                        <Divider />
                        {releaseBranchComponent(isInEmptyState)}
                        <Divider />
                        {versionInCodeComponent(
                            'working branch',
                            shouldShowBumpStatusComponentForWorkingBranch,
                            workingBranchCodeData,
                            isInEmptyState,
                        )}
                        {shouldShowVersionInCodeComponentForReleaseBranch && (
                            <>
                                <Divider />
                                {versionInCodeComponent(
                                    'release branch',
                                    shouldShowBumpStatusComponentForReleaseBranch,
                                    releaseBranchCodeData,
                                    isInEmptyState,
                                )}
                            </>
                        )}
                        <Divider />
                        {previousTagComponent(isInEmptyState)}
                    </div>
                </div>
            )}
        </EmptyStateWrapper>
    );
};

export default KickoffInfo;
